import type { ChannelClient } from '@hibro/platform-core';
import type {
  ILauncherService,
  IUpdateService,
  UpdateIdentity,
  UpdateState,
} from '@hibro/shared';
import {
  canRequestUpdateCheck,
  NO_UPDATE_CAPABILITIES,
  shouldShowUpdateConnecting,
  shouldShowUpdatePrompt,
  UPDATE_CHANGED_EVENT,
} from '@hibro/shared';
import {
  Button,
  DialogDescription,
  DialogTitle,
  Modal,
} from '@hibro/ui-client';
import { type ReactNode, useCallback, useEffect, useState } from 'react';

import type { OpenExternalBridge } from '../app-links/open';
import { useT } from '../i18n/i18n';
import updateArrow from './assets/update-arrow.svg';

const UPDATE_ACTION_CLASS =
  'min-h-10 h-auto min-w-0 flex-1 rounded-xl border-(--app-base-white-a30) px-3 py-2 text-[16px] leading-6 font-normal whitespace-normal focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';
const UPDATE_PRIMARY_CLASS = `${UPDATE_ACTION_CLASS} shadow-[0_0_6px_rgba(0,0,0,0.08)] backdrop-blur-[15px]`;

/**
 * Application update surfaces (见 25): the prompt every runtime shares, and the
 * About section in settings.
 *
 * **Both read one state that lives in the extension host.** Nothing here decides
 * anything about updating — not whether one exists, not whether it is mandatory,
 * not whether a dismissal still holds. That is the point: the workbench can have
 * several windows plus detached ones, and "a download is at 40%" has to be one
 * fact rather than one per window. These components render it and call verbs.
 *
 * The desktop and the browser differ in exactly two places, both driven by
 * `UpdateCapabilities` rather than by asking which runtime this is:
 * `canDownload` decides whether there is a fetch step before the swap, and
 * `canSelfInstall` decides whether the primary action installs or sends the user
 * to a download page.
 */

/** Subscribe to the host's update state (initial pull + broadcast). */
export function useUpdateState(
  updateService: IUpdateService,
  client: ChannelClient,
): UpdateState {
  const [state, setState] = useState<UpdateState>({ status: 'idle' });
  useEffect(() => {
    let disposed = false;
    // Subscribe before pulling, and let any broadcast win over the reply: the
    // initial `getState()` is a round trip, and a change that happens during it
    // would otherwise be overwritten by the older snapshot on arrival.
    let sawBroadcast = false;
    const dispose = client.listen(UPDATE_CHANGED_EVENT, (data) => {
      sawBroadcast = true;
      setState(data as UpdateState);
    });
    void updateService.getState().then((current) => {
      if (!disposed && !sawBroadcast) {
        setState(current);
      }
    });
    return () => {
      disposed = true;
      dispose();
    };
  }, [updateService, client]);
  return state;
}

/** Read who this build is and what it can do about updates (one-shot). */
function useUpdateIdentity(
  updateService: IUpdateService,
): UpdateIdentity | null {
  const [identity, setIdentity] = useState<UpdateIdentity | null>(null);
  useEffect(() => {
    let active = true;
    void updateService.getIdentity().then((next) => {
      if (active) {
        setIdentity(next);
      }
    });
    return () => {
      active = false;
    };
  }, [updateService]);
  return identity;
}

export interface UpdatePromptProps {
  updateService: IUpdateService;
  client: ChannelClient;
  /** Opens the download page when this runtime cannot install by itself (见 21 / 18). */
  launcherService: ILauncherService;
  openExternalBridge?: OpenExternalBridge;
}

/**
 * The update prompt (见 25). Mounted persistently by the workbench; it renders
 * nothing until the host says there is something to show, so there is no
 * "should I open it" state on this side to drift out of step with the host.
 *
 * A mandatory update makes the modal non-dismissable and drops「稍后」. That is
 * the whole of "forced update" as far as this component is concerned — locking
 * the workbench itself is the app gate's job (见 25 强制更新), not a modal's.
 */
export function UpdatePrompt({
  updateService,
  client,
  launcherService,
  openExternalBridge,
}: UpdatePromptProps): ReactNode {
  const t = useT();
  const state = useUpdateState(updateService, client);
  const identity = useUpdateIdentity(updateService);
  const capabilities = identity?.capabilities ?? NO_UPDATE_CAPABILITIES;

  // Same rule `openAppLink` follows: the browser needs the click's own gesture,
  // so its bridge wins; the desktop routes through the host.
  const openDownloadPage = useCallback(
    (url: string) => {
      if (openExternalBridge) {
        openExternalBridge.open(url);
        return;
      }
      void launcherService.openExternal(url);
    },
    [openExternalBridge, launcherService],
  );

  if (!shouldShowUpdatePrompt(state) || !identity) {
    return null;
  }
  // Narrowed by `shouldShowUpdatePrompt`, which only passes states carrying an
  // offer; the guard is here so this file never indexes a union member blindly.
  if (
    state.status !== 'available' &&
    state.status !== 'downloading' &&
    state.status !== 'ready'
  ) {
    return null;
  }
  const { info, mandatory } = state;
  const downloadUrl = info.downloadUrl;

  const actions: ReactNode[] = [];
  if (state.status === 'ready') {
    // What applying it ends with, from the capability rather than from asking
    // which OS this is (见 25 能力自报): a Windows installer replaces the app and
    // brings it back, while opening a macOS `.dmg` leaves the last step to the
    // user — so「重启并安装」would be a promise this platform cannot keep.
    actions.push(
      <Button
        key="install"
        className={UPDATE_PRIMARY_CLASS}
        onClick={() => void updateService.install()}
      >
        {t(
          capabilities.installNeedsUser
            ? 'update.prompt.openInstaller'
            : 'update.prompt.restart',
        )}
      </Button>,
    );
  } else if (state.status === 'downloading') {
    // A percentage only when there is one. platform-services reports no
    // progress — it answers once the package is on disk — so a bare「0%」would
    // sit there for the whole download looking stuck (见 25 进度).
    actions.push(
      <Button key="progress" className={UPDATE_PRIMARY_CLASS} disabled>
        {state.percent > 0
          ? t('update.prompt.downloading', { percent: String(state.percent) })
          : t('update.prompt.downloadingUnknown')}
      </Button>,
    );
  } else if (capabilities.canSelfInstall && !capabilities.canDownload) {
    // No fetch step (the browser): the offered build is already being served.
    actions.push(
      <Button
        key="reload"
        className={UPDATE_PRIMARY_CLASS}
        onClick={() => void updateService.install()}
      >
        {t('update.prompt.reload')}
      </Button>,
    );
  } else if (capabilities.canDownload) {
    actions.push(
      <Button
        key="download"
        className={UPDATE_PRIMARY_CLASS}
        onClick={() => void updateService.download()}
      >
        {t('update.prompt.download')}
      </Button>,
    );
  } else if (downloadUrl) {
    // This installation cannot apply a package at all (a Linux deb/rpm needs the
    // distribution's package manager): say so with a link rather than a button
    // that would fail (见 25 能力自报).
    actions.push(
      <Button
        key="page"
        className={UPDATE_PRIMARY_CLASS}
        onClick={() => openDownloadPage(downloadUrl)}
      >
        {t('update.prompt.openDownloadPage')}
      </Button>,
    );
  }
  if (!mandatory) {
    actions.unshift(
      <Button
        key="later"
        variant="secondary"
        className={`${UPDATE_ACTION_CLASS} bg-(--app-base-black-a6) text-(--app-color-text-secondary) dark:bg-(--app-base-white-a6)`}
        onClick={() => void updateService.dismiss()}
      >
        {t('update.prompt.later')}
      </Button>,
    );
  }

  return (
    <Modal
      open
      dismissable={!mandatory}
      onOpenChange={(next) => {
        if (!next) {
          void updateService.dismiss();
        }
      }}
      size="sm"
      showClose={false}
      className="w-[360px] gap-1.5 rounded-[20px] border-(--app-base-white-a30) bg-(--app-color-bg-module-container-win) px-0 pt-4 pb-5 shadow-[0_0_16px_rgba(0,0,0,0.12)] backdrop-blur-[30px] sm:max-w-[360px]"
      footer={<div className="flex w-full gap-1.5 px-5">{actions}</div>}
    >
      <div className="flex min-h-[258px] flex-col items-center gap-2 px-5 text-center">
        <div className="relative h-[123px] w-[146px] shrink-0 overflow-hidden drop-shadow-[0_4px_16px_rgba(0,0,0,0.04)]">
          <img
            src={new URL('./assets/update-rocket.png', import.meta.url).href}
            alt=""
            className="pointer-events-none absolute top-[-8.33%] left-[-0.42%] h-[112.74%] w-[100.84%] max-w-none"
          />
        </div>
        <DialogTitle className="m-0 text-[17px] leading-[22px] font-semibold text-(--app-color-text-primary)">
          {t(
            mandatory ? 'update.prompt.titleMandatory' : 'update.prompt.title',
          )}
        </DialogTitle>
        <div className="flex max-w-full flex-wrap items-center justify-center gap-x-2 gap-y-1 text-[14px] leading-[22px]">
          <span className="flex min-w-0 items-center gap-1 text-(--app-color-text-secondary)">
            <span className="min-w-0 break-all">V {identity.version}</span>
            <span className="min-w-[31px] shrink-0 rounded-[3px] border border-(--app-base-black-a20) bg-(--app-base-black-a6) px-1 text-[10px] leading-[15px] dark:border-(--app-base-white-a20) dark:bg-(--app-base-white-a6)">
              {t('update.prompt.old')}
            </span>
          </span>
          <img
            src={updateArrow}
            alt=""
            className="h-2 w-6 shrink-0 dark:invert"
          />
          <span className="flex min-w-0 items-center gap-1 text-(--app-color-text-brand)">
            <span className="min-w-0 break-all">V {info.version}</span>
            <span className="min-w-[31px] shrink-0 rounded-[3px] border border-(--app-base-brand-a40) bg-(--app-base-brand-a10) px-1 text-[10px] leading-[15px]">
              {t('update.prompt.new')}
            </span>
          </span>
        </div>
        <DialogDescription className="m-0 mt-2 w-full max-w-[300px] text-[14px] leading-[22px] text-(--app-color-text-secondary)">
          {t(mandatory ? 'update.prompt.bodyMandatory' : 'update.prompt.body', {
            version: info.version,
            current: identity.version,
          })}
        </DialogDescription>
        {state.status === 'ready' ? (
          <p className="m-0 text-[14px] leading-[22px] text-(--app-color-text-secondary)">
            {t(
              info.automaticInstallExhausted
                ? 'update.prompt.readyManual'
                : capabilities.installNeedsUser
                  ? 'update.prompt.readyNeedsUser'
                  : capabilities.installsOnLaunch
                    ? 'update.prompt.readyOnLaunch'
                    : 'update.prompt.ready',
            )}
          </p>
        ) : null}
      </div>
      {info.notes ? (
        <div className="mt-4 flex flex-col gap-1 px-5 text-[14px] leading-[22px]">
          <span className="font-medium text-(--app-color-text-secondary)">
            {t('update.prompt.notes')}
          </span>
          <p className="m-0 break-words whitespace-pre-wrap text-(--app-color-text-primary)">
            {info.notes}
          </p>
        </div>
      ) : null}
    </Modal>
  );
}

export interface UpdateSectionProps {
  updateService: IUpdateService;
  client: ChannelClient;
}

/**
 * The settings「关于与更新」section (见 25): which build this is, and an explicit
 * check.
 *
 * The button stays enabled even when `update.autoCheck` is off — that switch
 * silences the polling, and "I want to know now" is not the thing anyone turns
 * off. It is disabled only where the runtime genuinely cannot check, and then
 * the row says why instead of leaving a button that does nothing.
 */
export function UpdateSection({
  updateService,
  client,
}: UpdateSectionProps): ReactNode {
  const t = useT();
  const state = useUpdateState(updateService, client);
  const identity = useUpdateIdentity(updateService);
  const capabilities = identity?.capabilities ?? NO_UPDATE_CAPABILITIES;

  // The retry loop lives in the host; this only says "the panel that asked is
  // gone" so a closed overlay does not keep poking the agent (见 25 HI-768).
  useEffect(() => {
    return () => {
      void updateService.cancelCheck();
    };
  }, [updateService]);

  return (
    <div className="flex items-center justify-between gap-3 py-2">
      <span className="flex flex-col gap-0.5">
        <span className="text-[13px]">
          {identity
            ? t('settings.update.build', {
                version: identity.version,
                channel: identity.channel,
              })
            : ''}
        </span>
        <span className="text-xs text-muted-foreground">
          {describeStatus(state, capabilities, t)}
        </span>
      </span>
      <span className="flex items-center gap-2">
        {/* macOS still needs an explicit hand-off to its DMG. Windows stages the
          downloaded installer for the next launch, so adding a settings
          action there would falsely imply the user must press it. */}
        {state.status === 'ready' && !capabilities.installsOnLaunch ? (
          <Button size="sm" onClick={() => void updateService.install()}>
            {t(
              capabilities.installNeedsUser
                ? 'update.prompt.openInstaller'
                : 'update.prompt.restart',
            )}
          </Button>
        ) : null}
        <Button
          variant="outline"
          size="sm"
          // The same rule the verb applies (见 25): greying out only for
          // `checking` left an enabled button that silently declined while a
          // package was downloading. `ready` stays enabled so a later release
          // can replace the staged one.
          disabled={!canRequestUpdateCheck(state, capabilities)}
          onClick={() => void updateService.check(true)}
        >
          {t('settings.update.check')}
        </Button>
      </span>
    </div>
  );
}

/**
 * The one line under the version. Split out so the mapping from state to copy is
 * readable in one place — it is the only thing in this section with any logic,
 * and every branch is a sentence the user reads.
 */
function describeStatus(
  state: UpdateState,
  capabilities: UpdateIdentity['capabilities'],
  t: (key: string, args?: Record<string, unknown>) => string,
): string {
  if (!capabilities.canCheck) {
    return t('settings.update.unsupported');
  }
  // Agent socket not up yet (刚装完的竞态): the next cadence tick will ask
  // again, so this is「连接中」whether the miss came from a button or the timer.
  if (shouldShowUpdateConnecting(state)) {
    return t('settings.update.connecting');
  }
  switch (state.status) {
    case 'checking':
      return t('settings.update.checking');
    case 'available':
      return t('settings.update.available', { version: state.info.version });
    // Distinct from `available`, because in both of these the check button is
    // disabled — and a greyed-out button next to「发现新版本」reads as broken,
    // while next to「正在下载」it reads as the reason.
    case 'downloading':
      return t('settings.update.downloading', { version: state.info.version });
    case 'ready':
      return t(
        state.info.automaticInstallExhausted
          ? 'settings.update.readyManual'
          : capabilities.installsOnLaunch
            ? 'settings.update.readyOnLaunch'
            : 'settings.update.ready',
        { version: state.info.version },
      );
    case 'error':
      // Say which step failed, not just that something did: a missing
      // `app-update.yml` fails at download time, and calling that "检查更新失败"
      // once sent the first person to debug it at the wrong end (见 25).
      return t(
        state.phase === 'download'
          ? 'settings.update.error.download'
          : state.phase === 'install'
            ? 'settings.update.error.install'
            : 'settings.update.error',
        { message: state.message },
      );
    case 'idle':
    default:
      return state.checkedAt === undefined
        ? ''
        : `${t('settings.update.upToDate')} ${t('settings.update.lastChecked', {
            time: new Date(state.checkedAt).toLocaleTimeString(),
          })}`;
  }
}
